import { Button, CircularProgress, IconButton, Menu, MenuItem, Select, TextField } from '@mui/material';
import { Add, Delete, Edit, KeyboardArrowDown, OpenInNew, Publish, Report } from '@mui/icons-material';
import { Box } from '@mui/system';
import React, { useCallback, useEffect, useState } from 'react';
import { container } from 'tsyringe';
import { PTApiService } from '../../../services/ptapi-service';
import './QnA.scss';
import { MoreHoriz, Send } from '@mui/icons-material';
import { filter } from 'rxjs/operators';
import { QnaService } from '../../../services/qna-service';
import { QnaAnswer, QnaQuestion } from '../../../models/qna';
import moment from 'moment';
import { AutoHeightStyledTabs, StackedStyledTab, TabPanel } from '../../tabs/StyledTabs';
import { LightTooltip } from '../../tooltip/LightTooltip';
import { compare, sleep, useSharedState } from '../../../utils/helpers';
import { ptPublicEvent, ptEventDetails } from '../../../models/ptEvent';
import { ApiService } from '../../../services/api-service';

const announcementTemplates = [{
    name: 'Q&A',
    template: 'Have questions for the presenter? Use the Q&A panel to ask yours or like other questions.',
    index: 0
}, {
    name: 'Interact live',
    template: 'This event supports live video interaction! Send your request by clicking the "raise hand" icon.',
    index: 1
}, {
    name: 'Free text',
    template: '',
    index: 2
}
];

const QuestionDetails = ({ question, publishQuestion, declineQuestion, postAnswer, deleteQuestion, attendeeShortUrl, animation }:
    { question: QnaQuestion, publishQuestion: (question: QnaQuestion) => any, declineQuestion: (question: QnaQuestion) => any, postAnswer: (question: QnaQuestion, answer: string) => any, deleteQuestion: (question: QnaQuestion) => any, attendeeShortUrl: string, animation?: 'published' | 'declined' }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [answerText, setAnswerText] = useState('');
    const [answers, setAnswers] = useState<QnaAnswer[]>([]);
    const [ptApiService] = useState(container.resolve(PTApiService));
    const [answersToggled, setAnswersToggled] = useState(false);
    const [openReplyForm, setOpenReplyForm] = useState(false);
    const [hiddenAnimation, setHiddenAnimation] = useState(true);
    const [shownAnimation, setShownAnimation] = useState(false);

    const menuOpen = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const answerSubmit = useCallback(() => {
        if (!answerText) {
            return;
        }
        postAnswer(question, answerText);
        setAnswerText('');
    }, [answerText]);

    const fetchAnswers = async () => {
        // setAnswers(await ptApiService.fetchAnswers(question.id));
    };

    useEffect(() => {
        if (answers.length > 0 && (question.answerCount > 0 || question.replyCount > 0)) {
            fetchAnswers();
        }
    }, [question.answerCount, question.replyCount]);

    const toggleAnswers = useCallback(() => {
        if (answersToggled) {
            setAnswersToggled(false);
        } else {
            fetchAnswers();
            setAnswersToggled(true);
        }
    }, [answersToggled]);

    const viewQAEngagement = useCallback(() => {
        // window.open(attendeeShortUrl);
    }, [attendeeShortUrl]);

    const showAnimation = useCallback(async () => {
        setHiddenAnimation(false);
        await sleep(1);
        setShownAnimation(true);
        await sleep(2000);
        setShownAnimation(false);
        await sleep(500);
        setHiddenAnimation(true);
    }, []);

    useEffect(() => {
        if (animation && animation === 'published' && question.status === 'ACTIVE') {
            showAnimation();
        } else if (animation && animation === 'declined' && question.status === 'ASKED') {
            showAnimation();
        }
    }, [animation, question]);

    return <Box className="question-item-container" textAlign="left" padding={2} marginLeft={2} marginRight={2} marginBottom={2} display="flex" flexDirection="column">
        <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
                {question.status === 'DECLINED' && <div className="question-declined-label">Declined</div>}
                {question.status !== 'DECLINED' && <div className={`question-dot question-${question.answerCount ? 'answered' : (question.status === 'ACTIVE' ? 'active' : 'asked')}`}></div>}
            </Box>
            <Box>
                {['ACTIVE', 'DECLINED'].includes(question.status) && <IconButton
                    aria-controls="poll-menu"
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                >
                    <MoreHoriz />
                </IconButton>}
                <Menu
                    id="poll-menu"
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleClose}
                >
                    {['ACTIVE', 'DECLINED'].includes(question.status) && <MenuItem onClick={() => { handleClose(); deleteQuestion(question); }}>
                        <Box display="flex" alignItems="center" justifyContent="center" color="#565454" fontSize="11px">
                            <Delete fontSize="small" />
                            <Box marginLeft={1} height="20px" lineHeight="20px">Delete Question</Box>
                        </Box>
                    </MenuItem>}
                    {question.status === 'DECLINED' && <MenuItem onClick={() => { handleClose(); publishQuestion(question); }}>
                        <Box display="flex" alignItems="center" justifyContent="center" color="#565454" fontSize="11px">
                            <Publish fontSize="small" />
                            <Box marginLeft={1} height="20px" lineHeight="20px">Publish</Box>
                        </Box>
                    </MenuItem>}
                    {['ACTIVE', 'DECLINED'].includes(question.status) && <MenuItem onClick={() => { handleClose(); viewQAEngagement(); }}>
                        <Box display="flex" alignItems="center" justifyContent="center" color="#565454" fontSize="11px">
                            <OpenInNew fontSize="small" />
                            <Box marginLeft={1} height="20px" lineHeight="20px">View QA engagement</Box>
                        </Box>
                    </MenuItem>}
                </Menu>
            </Box>
        </Box>
        <Box className="question-text" textAlign="left" marginTop={1} marginBottom={1}>
            {question.content}
        </Box>
        <Box className="question-information" marginTop={2} marginBottom={2} display="flex" justifyContent="flex-start" align-items="center">
            <b>{question.anonymous ? 'anonymous' : question.initiator?.name}</b>
            <Box>{moment(question.timestamp).fromNow()}</Box>
            <Box>{question.upvoteCount} {question.upvoteCount > 1 ? 'Likes' : 'Like'}</Box>
        </Box>
        {question.answerCount > 0 && <Box textAlign="left" marginLeft={-2} marginRight={-2}>
            <Box display="flex" paddingLeft={2} paddingRight={2} justifyContent="space-between" alignItems="center" borderTop="1px solid #cbd2da" borderBottom={answersToggled ? '1px solid #cbd2da' : 'none'}>
                <Box>
                    <Box className="answers-count" marginRight={2}>{question.answerCount} {question.answerCount > 1 ? 'answers' : 'answer'} {question.replyCount > 0 && ` - ${question.replyCount} ${question.replyCount > 1 ? 'replies' : 'reply'}`}</Box>
                </Box>
                <Box>
                    <IconButton onClick={() => toggleAnswers()} className={answersToggled ? 'reversed-btn' : ''}>
                        <KeyboardArrowDown />
                    </IconButton>
                </Box>
            </Box>
            {answersToggled && answers.length > 0 && <Box padding={3}>
                {answers.map((a, i) => <Box className="answer-container" marginBottom={2} key={i} padding={2}>
                    <Box className="question-information" display="flex" justifyContent="flex-start" align-items="center">
                        <b>{a.anonymous || !a.answerBy ? 'anonymous' : a.answerBy.name}</b>
                        <Box>{moment(a.timestamp).fromNow()}</Box>
                    </Box>
                    <Box marginTop={1}>{a.content}</Box>
                </Box>)}
            </Box>}
        </Box>}
        {question.status === 'ACTIVE' && <Box borderTop="1px solid #cbd2da" marginLeft={-2} marginRight={-2} padding={2} paddingTop={2} paddingBottom={0}>
            {openReplyForm && <form onSubmit={(e) => { e.preventDefault(); answerSubmit(); }}>
                <Box display="flex" paddingTop={2} borderRadius="6px" bgcolor="#f8f8f8" padding={1}>
                    <TextField value={answerText} onChange={e => setAnswerText(e.target.value)} className="answer-input" variant='standard' placeholder='Reply...' multiline={true} InputProps={{ disableUnderline: true }}></TextField>
                    <Box display="flex" paddingLeft={1} alignItems="flex-end">
                        <IconButton type="submit">
                            <Send />
                        </IconButton>
                    </Box>
                </Box>
            </form>}
            {!openReplyForm && <Box className="reply-row" display="flex" justifyContent="flex-start" alignItems="center" onClick={() => setOpenReplyForm(true)}>
                <div className="reply-icon"></div>
                <Box marginLeft={1} color="#4a4a4a">Reply</Box>
            </Box>}
        </Box>}
        {question.status === 'ASKED' && <Box display="flex" alignItems="center" justifyContent="flex-end" borderTop="1px solid #cbd2da" marginTop={1} marginLeft={-2} marginRight={-2} padding={2} paddingTop={2} paddingBottom={0}>
            <LightTooltip title={
                <React.Fragment>
                    <Box display="flex" className="studio-light-tooltip-content" justifyContent="center" alignItems="center">
                        <Report />
                        <Box marginLeft={1}>
                            Declined questions will be accessible in the incoming tab.
                        </Box>
                    </Box>
                </React.Fragment>
            }>
                <button className="studio-cancel-btn question-accept-btn" onClick={() => declineQuestion(question)}>Decline</button>
            </LightTooltip>

            <button className="studio-primary-btn question-accept-btn" onClick={() => publishQuestion(question)}>Publish</button>
        </Box>}
        {!hiddenAnimation && animation === 'published' && <div className={`question-item-animation-mask ${shownAnimation ? 'show' : ''}`}>
            <div className="question-item-animation-mask-content">
                <div className="question-item-animation-icon"></div>
                <div className="question-item-animation-label">Yay! Question is successfully published!</div>
            </div>
        </div>}
        {!hiddenAnimation && animation === 'declined' && <div className={`question-item-animation-mask ${shownAnimation ? 'show' : ''}`}>
            <div className="question-item-animation-mask-content">
                <div className="question-item-animation-declined-icon"></div>
                <div className="question-item-animation-label">Question is not published!</div>
            </div>
        </div>}
    </Box >;
};

const Announcement = ({ close, attendeeShortUrl }: { close: () => any, attendeeShortUrl: string }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isSendingRequest, setIsSendingRequest] = useState(false);
    const [ptApiService] = useState(container.resolve(PTApiService));
    const [apiService] = useState(container.resolve(ApiService));
    const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(0);
    const [announcementText, setAnnouncementText] = useState(announcementTemplates.find(a => a.index === selectedTemplateIndex)?.template);

    const menuOpen = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        setIsFormValid(announcementText && announcementText.length > 0);
    }, [announcementText]);

    const templateSelected = (e) => {
        setSelectedTemplateIndex(e.target.value);
        setAnnouncementText(announcementTemplates.find(a => a.index === e.target.value)?.template);
    };

    const sendAnnouncement = useCallback(async () => {
        apiService.toggleRegisterPopup();
        // setIsSendingRequest(true);

        // try {
        //     // await ptApiService.sendAnnouncement(announcementText);
        //     close();
        // } catch (e) {
        //     console.error(e);
        //     alert(e?.message || e);
        // } finally {
        //     setIsSendingRequest(false);
        // }
    }, [announcementText]);

    const viewQAEngagement = useCallback(() => {
        // window.open(attendeeShortUrl);
    }, [attendeeShortUrl]);

    return <Box className="announcement-container" textAlign="left" margin={2} display="flex" flexDirection="column">
        <Box className="announcement-title" display="flex" alignItems="center" justifyContent="space-between" marginTop={1} marginBottom={1} padding={1} paddingLeft={2}>
            <Box>Send an Announcement</Box>
            <Box>
                <IconButton
                    aria-controls="poll-menu"
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                >
                    <MoreHoriz />
                </IconButton>
                <Menu
                    id="poll-menu"
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleClose}
                >
                    <MenuItem onClick={() => { handleClose(); viewQAEngagement(); }}>
                        <Box display="flex" alignItems="center" justifyContent="center" color="#565454" fontSize="11px">
                            <OpenInNew fontSize="small" />
                            <Box marginLeft={1} height="20px" lineHeight="20px">View QA engagement</Box>
                        </Box>
                    </MenuItem>
                </Menu>
            </Box>
        </Box>
        <Box display="flex" padding={2} paddingTop={1} flexDirection="column">
            <Box marginBottom={1} display="flex" justifyContent="stretch">
                <Select
                    value={selectedTemplateIndex}
                    onChange={templateSelected}
                    fullWidth={true}
                >
                    {announcementTemplates.map(t => <MenuItem key={t.index} value={t.index}>{t.name}</MenuItem>)}
                </Select>
            </Box>
            <Box display="flex" justifyContent="stretch">
                <TextField value={announcementText} onChange={e => setAnnouncementText(e.target.value)} className="answer-input" variant='outlined' placeholder='Type your message...' multiline={true} minRows={5}></TextField>
            </Box>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="flex-end" padding={2} paddingTop={4}>
            <button className="studio-cancel-btn announcement-form-btn" onClick={() => close()}>Cancel</button>
            <button className="studio-primary-btn announcement-form-btn" disabled={!isFormValid || isSendingRequest} onClick={() => { if (isFormValid) { sendAnnouncement(); } }}>Send</button>
        </Box>
    </Box>;
};

const NoQuestion = ({ mode, attendeeShortUrl, sendAnnouncement }: { mode: 'published' | 'answered' | 'unanswered', attendeeShortUrl: string, sendAnnouncement: () => any }) => {
    return <Box className="no-question-container" height="100%">
        <Box paddingTop={7} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
            <div className="no-question-icon"></div>
            <Box marginTop={2}>There are no question yet!</Box>
            <Box marginTop={2}>
                <button className="studio-primary-btn announcement-btn" onClick={() => sendAnnouncement()}>Send an announcement</button>
            </Box>
            {attendeeShortUrl && <Box marginTop={4}>
                <a className="share-link" href={attendeeShortUrl} target="_blank" rel="noreferrer">
                    <Box marginBottom={2} paddingLeft={1} alignItems="center" justifyContent="flex-start" display="flex" lineHeight="24px">
                        <OpenInNew />
                        <Box marginLeft={1}>View QA engagement</Box>
                    </Box>
                </a>
            </Box>}
        </Box>
    </Box>;
};

export const QnA = () => {
    const [ptApiService] = useState(container.resolve(PTApiService));
    const [qnaService] = useState(container.resolve(QnaService));
    const [questions, setQuestions] = useState<QnaQuestion[]>(null);
    const [attendeeShortUrl, setAttendeeShortUrl] = useState<string>(null);
    const [tabValue, setTabValue] = useState(1);
    const [announcementView, setAnnouncementView] = useState(false);
    const [publishedQuestions, setPublishedQuestions] = useState<QnaQuestion[]>(null);
    const [answeredQuestions, setAnsweredQuestions] = useState<QnaQuestion[]>(null);
    const [unansweredQuestions, setUnansweredQuestions] = useState<QnaQuestion[]>(null);
    const [incomingQuestions, setIncomingQuestions] = useState<QnaQuestion[]>(null);
    const [sortBy, setSortBy] = useState<'LATEST_FIRST' | 'LATEST_LAST'>('LATEST_FIRST');
    const [justPublishedQuestionId, setJustPublishedQuestionId] = useState('');
    const [justDeclinedQuestionId, setJustDeclinedQuestionId] = useState('');
    const [currentEvent] = useSharedState(ptApiService.ptCurrentEventInformation$) as [[ptPublicEvent, ptEventDetails]];

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        qnaService.fetchQuestions();
    };

    useEffect(() => {
        const subs = [
            qnaService.questionsObservable.subscribe(newQuestions => {
                setQuestions(newQuestions);
                qnaService.markQuestionIdsAsSeen(newQuestions.map(q => q.id))
            }),
            // ptApiService.ptCurrentEventInformation$.pipe(filter(a => a && a.length > 1)).subscribe(([e1]) => setAttendeeShortUrl(e1.pollShortUrl))
        ];

        qnaService.fetchQuestions();
        return () => subs.forEach(s => s.unsubscribe());
    }, []);

    useEffect(() => {
        if (questions) {
            const isAsc = sortBy === 'LATEST_LAST';
            setPublishedQuestions(questions.filter(q => q.status === 'ACTIVE').sort((a, b) => compare(a.timestamp, b.timestamp, isAsc)));
            setAnsweredQuestions(questions.filter(q => q.answerCount > 0).sort((a, b) => compare(a.timestamp, b.timestamp, isAsc)));
            setUnansweredQuestions(questions.filter(q => q.status === 'ACTIVE' && q.answerCount === 0).sort((a, b) => compare(a.timestamp, b.timestamp, isAsc)));
            setIncomingQuestions(questions.filter(q => q.status === 'ASKED' || q.status === 'DECLINED').sort((a, b) => compare(a.timestamp, b.timestamp, isAsc)));
        }
    }, [questions, sortBy]);

    const publishQuestion = useCallback(async (question: QnaQuestion) => {
        setJustPublishedQuestionId(question.id);
        // await ptApiService.approveDeclineQuestion(question.id, true);
        setTabValue(1);
    }, []);

    const declineQuestion = async (question: QnaQuestion) => {
        setJustDeclinedQuestionId(question.id);
        // await ptApiService.approveDeclineQuestion(question.id, false);
    };

    const deleteQuestion = async (question: QnaQuestion) => {
        // await ptApiService.deleteQuestion(question.id);
    };

    const postAnswer = async (question: QnaQuestion, answer: string) => {
        // await ptApiService.postAnswer(question.id, answer);
        setTabValue(2);
    };

    const getTabTitle = (text: string, tabQuestions: QnaQuestion[]) => {
        return <React.Fragment>
            <Box display="flex" justifyContent="center" alignItems="center">
                <Box>{text}</Box>
                {tabQuestions && tabQuestions.length > 0 && <Box className="studio-colored-tab-badge" marginLeft="5px">
                    {tabQuestions.length}
                </Box>}
            </Box>
        </React.Fragment>;
    };

    const getTabContent = useCallback((tabQuestions: QnaQuestion[]) => {
        return <Box>
            {tabQuestions !== null && <>
                {tabQuestions.length === 0 && !announcementView && <NoQuestion mode='published' attendeeShortUrl={attendeeShortUrl} sendAnnouncement={() => setAnnouncementView(true)} />}
                {tabQuestions.length === 0 && announcementView && <Announcement attendeeShortUrl={attendeeShortUrl} close={() => setAnnouncementView(false)} />}
                {tabQuestions.length > 0 && <Box textAlign="left">
                    <Box display="flex" justifyContent="flex-end">
                        <Box display="flex" justifyContent="center" alignItems="center" paddingRight={1}>
                            <Box paddingBottom="2px" marginRight={1} fontSize="13px">Sort by:</Box>
                            <Select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as any)}
                                variant="standard"
                                className="studio-borderless-select"
                            >
                                <MenuItem value={'LATEST_FIRST'}>Latest First</MenuItem>
                                <MenuItem value={'LATEST_LAST'}>Latest Last</MenuItem>
                            </Select>
                        </Box>
                    </Box>
                    {tabQuestions.map(q => <QuestionDetails question={q} key={q.id} declineQuestion={declineQuestion} deleteQuestion={deleteQuestion} postAnswer={postAnswer} publishQuestion={publishQuestion} attendeeShortUrl={attendeeShortUrl} animation={justPublishedQuestionId === q.id ? 'published' : (justDeclinedQuestionId === q.id ? 'declined' : null)} />)}
                </Box>}
            </>}
            {tabQuestions === null && <Box display="flex" width="100%" alignItems="center" justifyContent="center" paddingTop={3}>
                <CircularProgress />
            </Box>}
        </Box>
    }, [announcementView, attendeeShortUrl, sortBy, justPublishedQuestionId, justDeclinedQuestionId]);

    return <Box paddingTop={1} display="flex" flexDirection="column" className="qna-container">
        <Box sx={{ width: '100%' }}>
            <AutoHeightStyledTabs
                value={tabValue}
                onChange={handleTabChange}
            >
                {!currentEvent[1].qnaAutoApprove && <StackedStyledTab value={0} label={getTabTitle('Incoming', incomingQuestions)} />}
                <StackedStyledTab value={1} label={getTabTitle('Published', publishedQuestions)} />
                <StackedStyledTab value={2} label={getTabTitle('Answered', answeredQuestions)} />
                <StackedStyledTab value={3} label={getTabTitle('Unanswered', unansweredQuestions)} />
            </AutoHeightStyledTabs>
        </Box>
        {!currentEvent[1].qnaAutoApprove && <TabPanel className="full-height-tab" value={tabValue} index={0}>
            {getTabContent(incomingQuestions)}
        </TabPanel>}
        <TabPanel className="full-height-tab" value={tabValue} index={1}>
            {getTabContent(publishedQuestions)}
        </TabPanel>
        <TabPanel className="full-height-tab" value={tabValue} index={2}>
            {getTabContent(answeredQuestions)}
        </TabPanel>
        <TabPanel className="full-height-tab" value={tabValue} index={3}>
            {getTabContent(unansweredQuestions)}
        </TabPanel>
    </Box>;
}